<template>
  <div class="shop-the-look critical-component-hide">
    <overlay
      key="shopTheLook"
      block-class="shop-the-look"
      direction="up"
      namespace="shopTheLook"
      template
      :type="overlayType"
    >
      <template #body>
        <div class="main-product__content">
          <h2
            class="shop-the-look__title"
            v-text="$string('shop_the_look.title')"
          />

          <div class="shop-the-look__inner">
            <div class="shop-the-look__gallery-wrap">
              <div class="shop-the-look__gallery-sidebar">
                <button
                  v-for="thumbnail, index of collection"
                  :key="thumbnail.handle"
                  class="shop-the-look__gallery-thumbnail"
                  :class="{ 'active': index === collectionIndex }"
                  type="button"
                  @click="handleProductUpdate(thumbnail.handle)"
                >
                  <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
                  <img :src="(thumbnail.featuredImage.url)">
                </button>
              </div>

              <div class="shop-the-look__gallery-feature">
                <product-gallery
                  v-if="product"
                  :product="product"
                  shop-the-look
                  @swiper-ready="handleSwiperReady"
                />
              </div>
            </div>

            <div
              class="main-product__content-wrapper"
              @click="handleBodyClick($event)"
            >
              <div
                v-if="readTag('badge')"
                class="main-product__badge badge"
                v-text="readTag('badge')"
              />

              <h3
                class="main-product__title h3"
                v-text="getProductTitle"
              />

              <div class="main-product__prices-container">
                <product-prices
                  :available="product?.available"
                  block-class="main-product"
                  :compare-at-price="product?.compareAtPrice?.amount"
                  :price="product?.price?.amount"
                  :price-first="false"
                />
              </div>

              <template v-if="hasSwatches">
                <main-product-swatches
                  v-if="swatches.length"
                  :label="$string('product.form.color')"
                  :product="product"
                  :swatches="swatches"
                  @swatch-updated="handleSwatchUpdate"
                  @swatch-loaded="swatchLoaded"
                />
              </template>

              <div class="main-product__form">
                <product-form
                  :body-click="bodyClick"
                  :product="product"
                  :variant="variant"
                  shop-the-look
                  @update-variant="handleVariantUpdate"
                />
              </div>

              <a
                :href="`${root}products/${product.handle}`"
                class="shop-the-look__view-details"
                v-text="$string('shop_the_look.view_details')"
              />

              <div
                v-if="isMobile"
                class="shop-the-look__pagination"
              >
                <button
                  type="button"
                  class="shop-the-look__pagination--previous"
                  :class="{ 'disabled': collectionIndex === 0 }"
                  @click="handlePagination('previous')"
                >
                  <div class="shop-the-look__icon-wrap">
                    <icon-arrow-right />
                  </div>

                  <span v-text="$string('shop_the_look.previous')" />
                </button>

                <button
                  type="button"
                  class="shop-the-look__pagination--next"
                  :class="{ 'disabled': collectionIndex === (collection.length - 1) }"
                  @click="handlePagination('next')"
                >
                  <span v-text="$string('shop_the_look.next')" />

                  <div class="shop-the-look__icon-wrap">
                    <icon-arrow-right />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Shop the look (shop-the-look)
 * -----------------------------------------------------------------------------
 * Shop the Look Modal.
 *
 * @param {Number} limit - Number of products to show, from theme settings.
 *
 */
import { mapActions, mapState } from 'vuex'
import { formatProduct } from '~/helpers/format'
import { handleize } from '~/helpers/general'

import collection from '~/graphql/queries/collectionShopTheLook.gql'
import productWithMetafields from '~/graphql/queries/productWithMetafields.gql'
import productsWithMetafields from '~/graphql/queries/productsWithMetafields.gql'

import MainProductSwatches from '~async/main-product/main-product-swatches/main-product-swatches'
import Overlay from '~global/overlay/overlay'
import ProductForm from '~async/product-form/product-form'
import ProductGallery from '~async/product-gallery/product-gallery'
import ProductPrices from '~global/product-prices/product-prices'
import IconArrowRight from '~icons/direction/arrow/right-small.svg'

export default {
  name: 'ShopTheLook',

  components: {
    IconArrowRight,
    MainProductSwatches,
    Overlay,
    ProductForm,
    ProductGallery,
    ProductPrices,
  },

  props: {
    limit: {
      type: Number,
      default: 6,
    },
  },

  data() {
    return {
      bodyClick: 0,
      collection: [],
      collectionIndex: 0,
      hasSwatches: false,
      masterTitle: '',
      product: false,
      swatches: [],
      swatchProducts: [],
      swiper: null,
      variant: false,
      root: window.Shopify.routes.root,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute product title.
     * @returns {String}
     */
    getProductTitle() {
      if (!this.product) {
        return ''
      }

      let title = this.product.title

      if (title.includes(' - ')) {
        title = title.split(' - ')[0]
      }

      return title
    },

    /**
     * Compute overlay type based on screen size.
     * @returns {String}
     */
    overlayType() {
      if (this.isMobile) {
        return 'drawer'
      }

      return 'modal'
    },
  },

  mounted() {
    this.setEventBusListener()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Listen for 'shop-the-look' event to open modal.
     */
    setEventBusListener() {
      window.cnvs.EventBus.listen('shop-the-look', (data) => {
        this.getCollectionProductHandles(data.collection)

        this.toggleOverlay({
          ignoreDismissed: true,
          namespace: 'shopTheLook',
        })
      })
    },

    /**
     * Set Swiper object when initialized.
     * @param {Object} payload - Payload from gallery emit.
     */
    handleSwiperReady(payload) {
      this.swiper = payload
    },

    /**
     * Reset Swiper when switching between products.
     */
    resetSwiper() {
      if (!this.swiper) {
        return
      }

      this.swiper.update()
      this.swiper.slideTo(0)
    },

    /**
     * Handle click on pagination buttons.
     * @param {String} direction - 'previous' or 'next'.
     */
    handlePagination(direction) {
      if (direction === 'previous') {
        if (this.collectionIndex === 0) {
          return
        }

        this.collectionIndex -= 1

      } else {
        if (this.collectionIndex === (this.collection.length - 1)) {
          return
        }

        this.collectionIndex += 1
      }

      const handle = this.collection[this.collectionIndex].handle
      const container = document.querySelector('.shop-the-look__container.drawer__container')

      this.queryProduct(handle)
      this.resetSwiper()

      container.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    },

    /**
     * Handle click on product form to close custom select.
     * @param {Event} event - Click event.
     */
    handleBodyClick(event) {
      const customSelect = document.querySelector('.shop-the-look .custom-select')

      if (customSelect.contains(event.target)) {
        return
      }

      this.bodyClick = Date.now()
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.variant = variant
    },

    /**
     * Handle product update on thumbnail click.
     * @param {String} handle - New product handle.
     */
    handleProductUpdate(handle) {
      this.queryProduct(handle)

      const handles = this.collection.map((obj) => {
        return obj.handle
      })

      this.collectionIndex = handles.indexOf(handle)
      this.resetSwiper()
    },

    /**
     * Update product form with data from newly selected swatch.
     * @param {Object} swatch - Selected swatch.
     */
    handleSwatchUpdate(swatch) {
      const product = this.swatchProducts.find((data) => data.id === swatch.id)

      if (!product) {
        return
      }

      this.handleProductUpdate(product.handle)
    },

    /**
     * Check product to see if it has color as an option.
     * @param {Object} product - Product object.
     * @returns {Object}
     */
    checkForColorOption(product) {
      const color = product.options.find((option) => option.name.toLowerCase() === 'color')

      return color
    },

    /**
     * Get tag value.
     * @param {String} id - Text to match.
     * @returns {Boolean, String} - Value of tag.
     */
    readTag(id) {
      if (!this.product) {
        return false
      }

      const tag = this.product.tags.find((data) => data.split(':')[0] === id)

      if (!tag) {
        return false
      }

      const tagData = tag.split(':')

      return tagData[tagData.length - 1]
    },

    /**
     * Add failed attribute to swatch images that 404.
     * @param {Object} swatch - Swatch object.
     */
    swatchLoaded(swatch) {
      swatch.loaded = true
    },

    /**
     * Format swatchSrc.
     * @param {Object} color - Product color option.
     * @param {Array} tags - Product tags.
     * @returns {String}
     */
    formatSwatchSrc(color, tags) {
      const location = window.cnvs.urls.files.split('placeholder')[0]
      const key = handleize(color.values[0])
      const fabricTag = tags.find((tag) => tag.toLowerCase().includes('fabric'))
      let swatchSrc = `${location}color`

      if (fabricTag) {
        const fabric = fabricTag.split(':')[1]
          .split(' ')
          .join('-')
          .toLowerCase()
          .trim()

        swatchSrc = `${swatchSrc}-${fabric}`
      }

      swatchSrc = `${swatchSrc}-${key}_32x10.png`

      return swatchSrc
    },

    /**
     * Get list of all relevant swatch images sorted by Product ID.
     */
    getSwatchData() {
      this.swatches = this.swatchProducts.map((product) => {
        const color = this.checkForColorOption(product)

        if (!color) {
          return false
        }

        const {
          available,
          compareAtPrice,
          handle,
          id,
          tags,
          price,
        } = product

        const swatchSrc = this.formatSwatchSrc(color, tags)
        const isSale = compareAtPrice.amount && compareAtPrice.amount > price.amount

        return {
          available,
          handle,
          id,
          image: swatchSrc,
          name: color.values[0],
          sale: isSale,
        }
      })
    },

    /**
     * Determine if queried product matches current product.
     * @param {String} title - Product title.
     * @returns {Boolean}
     */
    isProductMatch(title) {
      const masterTitle = this.masterTitle.toLowerCase()
      const newTitle = title.toLowerCase().split(' - ')[0].trim()

      return masterTitle === newTitle
    },

    /**
     * Callback after successful query for associated products.
     * @param {Object} response - Response from GraphQl.
     */
    handleSwatchProductsQuerySuccess(response) {
      const tmpSwatches = []

      response.products.edges.forEach((product) => {
        const data = { product: product.node }

        if (this.isProductMatch(data.product.title)) {
          tmpSwatches.push(formatProduct(data))
        }
      })

      const activeProduct = tmpSwatches.find((product) => this.product.id === product.id)
      const index = tmpSwatches.indexOf(activeProduct)

      tmpSwatches.splice(index, 1)
      tmpSwatches.unshift(activeProduct)

      this.swatchProducts = tmpSwatches
      this.getSwatchData()
    },

    /**
     * Load products from GraphQL and sort currently active product to front.
     */
    async querySwatchProducts() {
      const query = `(title:'${this.masterTitle}'*)`

      try {
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: productsWithMetafields,
          variables: {
            country: cnvs.store.country,
            query,
            namespace: 'descriptors',
            modelSizeCaptionKey: 'model_size_caption',
            preorderShippingDateKey: 'preorder_shipping_date',
            washKey: 'machine_wash',
            fabricKey: 'fabric',
            importedKey: 'imported',
            careGuideKey: 'care_guide_content',
            sizeFitKey: 'size_fit',
            featuresDetailsKey: 'features_details',
            styledWithKey: 'styled_with',
            sizeGuideKey: 'size_guide',
          },
        })

        if (!response.products.edges.length) {
          return
        }

        this.handleSwatchProductsQuerySuccess(response)

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Callback after successful product query.
     */
    handleProductQuerySuccess() {
      const color = this.checkForColorOption(this.product)

      if (color) {
        this.hasSwatches = true
      }

      const tempTitle = this.masterTitle

      this.masterTitle = this.product.title.split(' - ')[0].trim()

      if (!this.hasSwatches || tempTitle === this.masterTitle) {
        return
      }

      this.querySwatchProducts()
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle) {
      if (!handle || this.product.handle === handle) {
        return
      }

      try {
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: productWithMetafields,
          variables: {
            country: cnvs.store.country,
            handle,
            namespace: 'descriptors',
            modelSizeCaptionKey: 'model_size_caption',
            preorderShippingDateKey: 'preorder_shipping_date',
            washKey: 'machine_wash',
            fabricKey: 'fabric',
            importedKey: 'imported',
            careGuideKey: 'care_guide_content',
            sizeFitKey: 'size_fit',
            featuresDetailsKey: 'features_details',
            styledWithKey: 'styled_with',
            sizeGuideKey: 'size_guide',
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        this.product = formatProduct(response)
        this.variant = this.product.variants.find((variant) => variant.available)

        this.handleProductQuerySuccess()

      } catch (error) {
        throw new Error('Failed to load product', error)
      }
    },

    /**
     * Get collection product handles from GraphQL.
     * @param {String} handle - Product handle.
     * @param {Number} first - How many products to show, from theme settings.
     */
    async getCollectionProductHandles(handle, first = this.limit) {
      try {
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: collection,
          variables: {
            country: cnvs.store.country,
            language: cnvs.store.language,
            handle,
            first,
          },
        })

        this.collection = response.collection.products.edges.map((product) => {
          return product.node
        })

        if (!this.collection.length) {
          return
        }

        this.queryProduct(this.collection[0].handle)

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },
  },
}
</script>
